// Écran de jeu : monte le canvas, charge les sprites du thème choisi et pilote la
// boucle du moteur porté (src/game/*). La Promise se résout quand le joueur revient
// au menu (option « changer de style » ou BackButton Telegram).
import { tg, haptic, shareScore } from "./telegram";
import {
  startGameSession,
  submitScore,
  createInvoice,
  createShareCard,
  openChest,
  simulatePurchase,
  type ChestReward,
} from "./api";
import { setCoins, isAdmin, refreshEntitlements } from "./entitlements";
import { sfx } from "./sound";
import { Game } from "./game/game";
import { PixiRenderer, W as LOGICAL_W, H as LOGICAL_H } from "./game/renderer";
import { loadSprites } from "./game/assets";
import { UI } from "./game/i18n";
import type { Theme } from "./game/themes";
import type { Mode } from "./game/modes";
import type { Lang } from "./game/i18n";

export interface GameChoice {
  theme: Theme;
  lang: Lang;
  mode: Mode;
  booster?: string; // slow | double | shield (consommé pour la 1re manche)
}

/** Carte de partage (PNG) : fond sombre, gros score doré, identité du jeu. */
function buildShareCard(score: number): string {
  const c = document.createElement("canvas");
  c.width = 480;
  c.height = 320;
  const x = c.getContext("2d")!;
  x.fillStyle = "#15202b";
  x.fillRect(0, 0, 480, 320);
  x.fillStyle = "#ffd23f"; // liserés or
  x.fillRect(0, 0, 480, 6);
  x.fillRect(0, 314, 480, 6);
  x.textAlign = "center";
  x.fillStyle = "#fff";
  x.font = '20px "Press Start 2P", monospace';
  x.fillText("THE BIG DIG", 240, 58);
  x.font = "56px monospace";
  x.fillText("👃", 240, 128);
  x.fillStyle = "#ffd23f";
  x.font = 'bold 58px "Press Start 2P", monospace';
  x.fillText(String(score), 240, 212);
  x.fillStyle = "#9aa7b4";
  x.font = '12px "Press Start 2P", monospace';
  x.fillText("SCORE", 240, 244);
  x.fillStyle = "#fff";
  x.font = "16px monospace";
  x.fillText("t.me/thebigdig_bot/thebigdig", 240, 292);
  return c.toDataURL("image/png");
}

/** Lance une partie ; se résout quand le joueur revient au menu. */
export async function startGame(root: HTMLElement, choice: GameChoice): Promise<void> {
  const wrap = document.createElement("div");
  wrap.className = "game-screen";
  const canvas = document.createElement("canvas");
  canvas.className = "game-canvas";
  // Overlay HTML de fin de partie : plein écran, au-dessus du canvas (le jeu figé
  // reste visible derrière, flouté). Rempli par renderOverlay() à chaque fin.
  const overlay = document.createElement("div");
  overlay.className = "over";
  overlay.hidden = true;
  wrap.append(canvas, overlay);
  root.replaceChildren(wrap);

  // Sprites du thème puis couche de rendu PixiJS (WebGL) montée sur le canvas.
  // Échec d'init (WebGL indisponible) → retour au menu plutôt qu'un écran mort.
  const sprites = await loadSprites(choice.theme);
  let renderer: PixiRenderer;
  try {
    renderer = await PixiRenderer.create(canvas, sprites, choice.theme, UI[choice.lang]);
  } catch (e) {
    console.error("[pixi] init impossible :", e);
    haptic("error");
    return;
  }

  // Session de partie (anti-triche) : un jeton signé par partie, lié à thème+mode.
  // Le booster n'est consommé/gravé que pour la PREMIÈRE manche.
  const initData = tg?.initData ?? "";
  let currentSid: string | null = null;
  let firstSession = true;
  function refreshSession(): void {
    if (!initData) return;
    const booster = firstSession ? choice.booster : undefined;
    firstSession = false;
    void startGameSession(initData, { theme: choice.theme.key, mode: choice.mode.key, booster }).then(
      (r) => {
        currentSid = r.ok ? r.data.sid : null;
      },
    );
  }
  refreshSession(); // session pour la 1ʳᵉ partie

  // État de l'overlay de fin : pièces gagnées + coffre du jour (remplis en asynchrone
  // par la réponse de /scores, puis mis à jour en place dans le DOM).
  let earned = 0;
  let chest: { state: "none" | "ready" | "busy" | "done"; text: string; canReroll: boolean } = {
    state: "none",
    text: "",
    canReroll: false,
  };

  // En fin de partie : soumet le score lié à la session, puis en prépare une nouvelle.
  const onGameOver = (r: { score: number; theme: string; mode: string }) => {
    earned = 0; // repart à zéro : la réponse de l'API remplira
    chest = { state: "none", text: "", canReroll: false };
    if (initData && currentSid) {
      void submitScore(initData, { score: r.score, sid: currentSid }).then((res) => {
        if (res.ok) {
          setCoins(res.data.coins); // solde à jour
          earned = res.data.earned;
          if (res.data.chestAvailable) chest.state = "ready"; // 🎁 coffre du jour
          updateOverlayRewards(); // l'overlay est déjà affiché : maj en place
        }
      });
      currentSid = null; // un jeton = un seul score
    }
    refreshSession(); // prochaine partie (REJOUER)
  };

  // Partage du score : carte IMAGE via message préparé Telegram (repli : texte).
  const ui = UI[choice.lang];
  const onShare = () => {
    haptic("light");
    const score = game.currentScore;
    void (async () => {
      if (initData && tg?.shareMessage) {
        try {
          const r = await createShareCard(initData, buildShareCard(score), score);
          if (r.ok) {
            tg.shareMessage!(r.data.id);
            return;
          }
        } catch {
          /* repli texte ci-dessous */
        }
      }
      shareScore(ui.shareText.replace("{n}", String(score)));
    })();
  };
  const game = new Game(renderer, choice.theme, choice.lang, choice.mode, onGameOver);
  // Boosters actifs (le « double » est appliqué côté serveur via la session).
  if (choice.booster === "slow") game.setBoosts({ slow: true, icon: "🐌" });
  else if (choice.booster === "shield") game.setBoosts({ shield: 1, icon: "🛡️" });
  else if (choice.booster === "double") game.setBoosts({ icon: "💰" });

  // 🎁 Coffre du jour : ouverture côté serveur (gratuit puis relances 3⭐).
  const chestLabel = (r: ChestReward): string => {
    if (r.type === "coins") return `+${r.amount} 🪙 !`;
    if (r.type === "booster") {
      const icon = { slow: "🐌", double: "💰", shield: "🛡️" }[r.booster ?? ""] ?? "🎒";
      const name =
        r.booster === "slow" ? ui.boostSlow : r.booster === "double" ? ui.boostDouble : ui.boostShield;
      return `${icon} ${name} !`;
    }
    if (r.type === "mode")
      return ui.chestModeWin.replace("{name}", (ui.modeNames[r.mode ?? ""] ?? r.mode ?? "").toUpperCase());
    return ui.chestNothing;
  };
  // ---- Overlay HTML de fin de partie ----
  // Références vivantes vers les éléments mis à jour en asynchrone.
  let chipCoins: HTMLElement | null = null;
  let chestBtn: HTMLButtonElement | null = null;

  function el(tag: string, cls: string, text = ""): HTMLElement {
    const e = document.createElement(tag);
    e.className = cls;
    if (text) e.textContent = text;
    return e;
  }

  /** Bouton coffre : libellé + état selon la machine à états locale. */
  function updateChestBtn(): void {
    if (!chestBtn) return;
    chestBtn.hidden = chest.state === "none";
    chestBtn.disabled =
      chest.state === "busy" || (chest.state === "done" && !chest.canReroll);
    chestBtn.classList.toggle("over-chest-ready", chest.state === "ready");
    chestBtn.textContent =
      chest.state === "ready"
        ? ui.chestOpen
        : chest.state === "busy"
          ? "🎁 …"
          : chest.text + (chest.canReroll ? "  ·  ↻ 3⭐" : "");
  }

  /** Réponse de /scores arrivée après l'affichage : pièces gagnées + coffre. */
  function updateOverlayRewards(): void {
    if (chipCoins) {
      chipCoins.hidden = earned <= 0;
      chipCoins.textContent = `+${earned} 🪙`;
    }
    updateChestBtn();
  }

  /** Ouvre le coffre (gratuit) ou relance (payant : Stars ; admin → simulation). */
  function onChestClick(): void {
    const reroll = chest.state === "done";
    if (chest.state !== "ready" && !(reroll && chest.canReroll)) return;
    const prev = chest.state;
    chest.state = "busy";
    updateChestBtn();
    const restore = () => {
      chest.state = prev;
      updateChestBtn();
    };
    void (async () => {
      if (!initData) return restore();
      if (reroll) {
        if (isAdmin()) {
          const sim = await simulatePurchase(initData, "chest_reroll");
          if (!sim.ok) return restore();
        } else {
          const inv = await createInvoice(initData, "chest_reroll");
          if (!inv.ok || !tg?.openInvoice) return restore();
          const paid = await new Promise<boolean>((res) =>
            tg!.openInvoice(inv.data.link, (st) => res(st === "paid")),
          );
          if (!paid) return restore();
        }
      }
      const r = await openChest(initData);
      if (!r.ok) return restore();
      setCoins(r.data.coins);
      const reward = r.data.reward;
      if (reward.type === "booster" || reward.type === "mode") void refreshEntitlements(initData);
      if (reward.type === "nothing") sfx.miss();
      else if (reward.type === "mode" || (reward.type === "coins" && (reward.amount ?? 0) >= 200)) sfx.record();
      else sfx.win();
      haptic(reward.type === "nothing" ? "warning" : "success");
      chest = { state: "done", text: chestLabel(reward), canReroll: r.data.canReroll };
      updateChestBtn();
    })();
  }

  /** « Continue » 5⭐ : admin → revive direct (test) ; sinon facture Stars. */
  function onContinueClick(): void {
    if (isAdmin()) {
      haptic("success");
      game.revive();
      return;
    }
    if (!initData) return;
    void createInvoice(initData, "continue").then((r) => {
      if (!r.ok || !tg?.openInvoice) return void haptic("error");
      tg.openInvoice(r.data.link, (status) => {
        if (status === "paid") {
          haptic("success");
          game.revive();
        }
      });
    });
  }

  /** (Re)construit la carte de fin : verdict / score / récompenses / actions. */
  function renderOverlay(): void {
    const card = el("div", "over-card");
    card.classList.add(game.isWon ? "over-win" : game.isNewRecord ? "over-gold" : "over-lose");

    // Verdict (RÉUSSI / PLUS DE VIES / TEMPS ÉCOULÉ).
    const title = game.isWon ? ui.won : choice.mode.lives !== null ? ui.noLives : ui.timeUp;
    card.append(el("div", "over-title", title));

    // Score, énorme et net — doré si nouveau record.
    const scoreEl = el("div", "over-score" + (game.isNewRecord ? " over-score-gold" : ""), String(game.currentScore));
    card.append(scoreEl, el("div", "over-score-label", ui.score.toUpperCase()));

    // Chips : record (toujours visible) + pièces gagnées (dès la réponse API).
    const chips = el("div", "over-chips");
    const rec = el(
      "div",
      "over-chip" + (game.isNewRecord ? " over-chip-record" : ""),
      game.isNewRecord ? `🏆 ${ui.newRecord}` : `${ui.best} ${game.bestScore}`,
    );
    chipCoins = el("div", "over-chip over-chip-coins");
    chipCoins.hidden = true;
    chips.append(rec, chipCoins);
    card.append(chips);

    // Coffre du jour (caché tant que l'API n'a pas dit qu'il est disponible).
    chestBtn = el("button", "over-chest") as HTMLButtonElement;
    chestBtn.addEventListener("click", onChestClick);
    card.append(chestBtn);
    updateOverlayRewards();

    // Actions : REJOUER (primaire) · PARTAGER + MENU · CONTINUER (si perdu).
    const replay = el("button", "over-btn over-replay", ui.playAgain);
    replay.addEventListener("click", () => {
      haptic("light");
      game.reset();
    });
    const row = el("div", "over-row");
    const share = el("button", "over-btn over-share", `↗ ${ui.shareCta}`);
    share.addEventListener("click", onShare);
    const menuBtn = el("button", "over-btn over-menu", ui.changeStyle);
    menuBtn.addEventListener("click", () => {
      haptic("light");
      game.quit();
    });
    row.append(share, menuBtn);
    card.append(replay, row);
    if (game.canContinue) {
      const cont = el("button", "over-btn over-continue", `❤ ${ui.continueCta} · 5⭐`);
      cont.addEventListener("click", onContinueClick);
      card.append(cont);
    }
    overlay.replaceChildren(card);
  }

  await new Promise<void>((resolve) => {
    let exited = false;
    let raf = 0;

    // Écoute sur toute la fenêtre : sur mobile, taper dans les marges autour du
    // canvas doit compter. Tap DANS le canvas → comportement complet (bouton ⏸,
    // menus de fin/pause via la position). Tap HORS canvas → simple tentative.
    const onDown = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      const inside =
        e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (inside) {
        game.pointer(
          ((e.clientX - r.left) / r.width) * LOGICAL_W,
          ((e.clientY - r.top) / r.height) * LOGICAL_H,
        );
      } else if (!game.isOver && !game.isPaused) {
        game.attempt(); // tap hors canvas = tentative, UNIQUEMENT en jeu actif
      }
    };
    const onMove = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom)
        return; // survol pertinent seulement au-dessus du canvas (menus desktop)
      game.hover(
        ((e.clientX - r.left) / r.width) * LOGICAL_W,
        ((e.clientY - r.top) / r.height) * LOGICAL_H,
      );
    };
    const onKey = (e: KeyboardEvent) => {
      // Écran de fin (overlay HTML) : Entrée/Espace = rejouer, Échap = menu.
      if (game.isOver) {
        if (e.code === "Space" || e.code === "Enter") (e.preventDefault(), game.reset());
        else if (e.code === "Escape") (e.preventDefault(), game.quit());
        return;
      }
      if (e.code === "Space" || e.code === "Enter") (e.preventDefault(), game.attempt());
      else if (e.code === "ArrowUp") (e.preventDefault(), game.moveSelection(-1));
      else if (e.code === "ArrowDown") (e.preventDefault(), game.moveSelection(1));
      else if (e.code === "Escape" || e.code === "KeyP") (e.preventDefault(), game.togglePause());
    };

    function teardown(): void {
      if (exited) return;
      exited = true;
      cancelAnimationFrame(raf);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("keydown", onKey);
      tg?.BackButton.offClick(onBack);
      tg?.BackButton.hide();
      tg?.MainButton.hide();
      renderer.destroy(); // libère le GPU (textures du thème comprises)
    }

    function onBack(): void {
      haptic("light");
      teardown();
      resolve();
    }

    window.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("keydown", onKey);
    tg?.BackButton.show();
    tg?.BackButton.onClick(onBack);
    tg?.MainButton.hide();

    let last = performance.now();
    function frame(now: number): void {
      const dt = Math.min(0.05, (now - last) / 1000); // clamp anti-saut
      last = now;
      game.update(dt);
      game.render();
      // Overlay de fin : (ré)affiché à chaque fin de partie, masqué au rejouer/revive.
      if (game.isOver === overlay.hidden) {
        if (game.isOver) renderOverlay();
        overlay.hidden = !game.isOver;
      }
      if (game.wantsExit) {
        teardown();
        resolve();
        return;
      }
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);
  });
}
